import React from 'react';
import { AlertCircle, AlertTriangle, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ValidationError } from '@/lib/validation';

interface ValidationErrorsProps {
  errors: ValidationError[];
  warnings: ValidationError[];
  onClose?: () => void;
  onNodeClick?: (nodeId: string) => void;
}

export const ValidationErrors: React.FC<ValidationErrorsProps> = ({
  errors,
  warnings,
  onClose,
  onNodeClick,
}) => {
  if (errors.length === 0 && warnings.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-20 right-4 z-50 w-96 space-y-2 max-h-[calc(100vh-6rem)] overflow-y-auto">
      {errors.map((error, index) => (
        <Alert key={`error-${index}`} variant="destructive" className="relative pr-10">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Validation Error</AlertTitle>
          <AlertDescription className="mt-2">
            {error.message}
            {error.nodeId && onNodeClick && (
              <Button
                variant="link"
                size="sm"
                className="mt-2 p-0 h-auto text-red-600 hover:text-red-700"
                onClick={() => onNodeClick(error.nodeId!)}
              >
                Go to node →
              </Button>
            )}
          </AlertDescription>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </Alert>
      ))}

      {warnings.map((warning, index) => (
        <Alert key={`warning-${index}`} className="relative pr-10 border-yellow-500 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-800">Warning</AlertTitle>
          <AlertDescription className="mt-2 text-yellow-700">
            {warning.message}
            {warning.nodeId && onNodeClick && (
              <Button
                variant="link"
                size="sm"
                className="mt-2 p-0 h-auto text-yellow-600 hover:text-yellow-700"
                onClick={() => onNodeClick(warning.nodeId!)}
              >
                Go to node →
              </Button>
            )}
          </AlertDescription>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </Alert>
      ))}
    </div>
  );
};
